import { copy } from '@ohear/shared'
import { Spinner } from '@ohear/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
/**
 * Actor recording route — Pro Tools light surface for the recorder.
 *
 * Resolves the actor's active project + batch, then mounts the RecorderShell.
 * The route container only handles light/error/empty states + the top sticky
 * sub-nav with tab switcher; everything else lives in the lazy-loaded
 * `feature-recording` chunk so this page only pays its weight when an actor
 * actually navigates here.
 */
import { Suspense, lazy } from 'react'
import { useActorProject } from '../../../lib/api/projects'
import { useRequireRole } from '../../../lib/auth'
import { useT } from '../../../lib/i18n/use-t'

const RecorderShell = lazy(() =>
  import('../../../features/recording/RecorderShell').then((m) => ({
    default: m.RecorderShell,
  })),
)

export const Route = createFileRoute('/actor/projects/$projectId/record')({
  component: ActorRecordPage,
})

function ActorRecordPage() {
  useRequireRole(['actor'])
  const t = useT()
  const { projectId } = Route.useParams()
  const project = useActorProject(projectId)

  if (project.isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-pt-bg">
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-pt-text-soft">
          Loading project…
        </p>
      </div>
    )
  }
  if (project.isError) {
    const msg = project.error instanceof Error ? project.error.message : 'unknown'
    return (
      <div className="bg-pt-bg p-6">
        <p className="mb-2 inline-block rounded-sm border border-pt-text bg-pt-warn px-3 py-1.5 text-pt-text">
          Could not load project: {msg}
        </p>
        <div className="mt-3">
          <Link to="/actor" className="text-pt-wave hover:underline">
            Back to projects
          </Link>
        </div>
      </div>
    )
  }
  const data = project.data
  if (!data) return null

  // Pick the active batch — first in_progress, falling back to the lowest
  // not_started one (so a fresh actor lands on Batch 1). Server may omit
  // `batches` on a freshly-created project, so guard with empty array.
  const batches = data.batches ?? []
  const activeBatch =
    batches.find((b) => b.status === 'in_progress') ??
    batches.find((b) => b.status === 'not_started') ??
    batches[0]

  if (!activeBatch) {
    return (
      <div className="bg-pt-bg p-6">
        <p className="text-pt-text">{t(copy.common.EMPTY.no_batches_assigned)}</p>
        <Link to="/actor" className="text-pt-wave hover:underline">
          Back to projects
        </Link>
      </div>
    )
  }

  return (
    <section className="bg-pt-bg" style={{ minHeight: 'calc(100vh - 80px)' }}>
      {/* PT-style top sub-nav: chrome bar with LCD-ish meta + segmented tabs. */}
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-pt-text bg-pt-chrome px-4 py-1.5 text-[12px] shadow-[inset_0_1px_0_rgba(255,255,255,0.55),inset_0_-1px_0_rgba(0,0,0,0.18)]">
        <div className="flex items-center gap-2 font-mono uppercase tracking-[0.12em]">
          <Link
            to="/actor"
            className="text-pt-text hover:text-pt-wave transition-colors"
            aria-label="Back to projects"
          >
            ← Projects
          </Link>
          <span className="text-pt-text-soft">|</span>
          <span className="text-pt-text-soft">{data.client_name}</span>
          <span className="text-pt-text-soft">|</span>
          <span className="flex items-center gap-2 normal-case tracking-normal">
            {data.characters.map((c, i) => (
              <span key={c.character_id} className="flex items-center gap-2">
                {i > 0 ? <span className="text-pt-text-soft">/</span> : null}
                <span className="text-pt-wave font-bold">[{c.display_name}]</span>
                <span className="font-mono text-pt-text-soft">{c.character_code}</span>
              </span>
            ))}
          </span>
        </div>
        {/* PT segmented tab control (script-review / record / upload). */}
        <div className="inline-flex items-stretch overflow-hidden rounded-sm" role="group">
          <Link
            to="/actor/projects/$projectId/script-review"
            params={{ projectId }}
            className="pt-btn flex items-center px-3 py-1 text-[11px] rounded-l-sm rounded-r-none -mr-px"
          >
            Script review
          </Link>
          <Link
            to="/actor/projects/$projectId/record"
            params={{ projectId }}
            aria-pressed="true"
            className="pt-btn pt-btn--armed flex items-center px-3 py-1 text-[11px] rounded-none -mr-px"
            style={{
              background: 'linear-gradient(180deg, #ffe97a 0%, #ffd54f 100%)',
              boxShadow:
                'inset 0 2px 3px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(0,0,0,0.18), 0 0 8px rgba(255,213,79,0.65)',
            }}
          >
            Record
          </Link>
          <Link
            to="/actor/projects/$projectId/upload"
            params={{ projectId }}
            className="pt-btn flex items-center px-3 py-1 text-[11px] rounded-r-sm rounded-l-none"
          >
            {t(copy.upload.BATCH.TAB)}
          </Link>
        </div>
      </div>

      {/* Desktop / tablet ≥md: full recorder. Mobile: PT-light fallback panel. */}
      <div className="hidden md:block">
        <Suspense
          fallback={
            <div className="flex justify-center bg-pt-bg p-12">
              <Spinner size="lg" label="Loading recording UI…" />
            </div>
          }
        >
          <RecorderShell
            projectId={projectId}
            batchId={activeBatch.batch_id}
            characterCode={data.characters[0]?.character_code ?? ''}
            episodeTitle={data.title}
            batchNumber={activeBatch.batch_number}
            totalBatches={data.total_batches}
            frameRate={data.frame_rate}
          />
        </Suspense>
      </div>

      <div className="block p-6 text-center md:hidden">
        <div className="mx-auto max-w-sm rounded-sm border border-pt-border bg-pt-panel p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.55),0_2px_8px_rgba(0,0,0,0.18)]">
          <div className="mb-3 text-3xl" aria-hidden="true">
            🎙️
          </div>
          <h2 className="mb-2 text-base font-semibold text-pt-text">
            {t(copy.common.DEVICE_GATE.desktop_required_title)}
          </h2>
          <p className="mb-5 text-sm text-pt-text-soft">
            Recording requires a desktop browser with a microphone. Please open this on a laptop or
            desktop.
          </p>
          <Link to="/actor" className="inline-block pt-btn px-4 py-2 text-sm">
            ← Back to projects
          </Link>
        </div>
      </div>
    </section>
  )
}
